import { Timestamp } from '@angular/fire/firestore';

// ─── Booking State Machine ───────────────────────────────────────────────────
//
//  (Stripe checkout, no Firestore doc) ──► PENDING_FORMALIZATION ──► PENDING_VERIFICATION ──► CONFIRMED
//                                                    │                         │
//                                                    └─────────────────────────┴──► CANCELLED
//
//  PENDING_FORMALIZATION: sign + receipt within a window based on days until event.
//  (>30→7d, >15→3d, >1→1d, ≤1→until 1h before event). See formalization-deadline.utils.ts.
// ─────────────────────────────────────────────────────────────────────────────

export const RESERVATION_CONDITIONS_VERSION = '2026-V1';

export type FormalizationWindowDays = 0 | 1 | 3 | 7;
export type FormalizationDeadlineMode = 'payment_plus_days' | 'event_minus_1h';

export type BookingStatus =
  | 'PENDING_FORMALIZATION'
  | 'PENDING_VERIFICATION'
  | 'CONFIRMED'
  | 'ACTIVE'
  | 'CANCELLED'
  | 'COMPLETED'
  | 'MANUAL_BLOCK';

export interface PlatformFeeAcceptanceLog {
  accepted: true;
  version: string;
  acceptedAt?: Timestamp;
  acceptanceIp?: string;
  userAgent?: string;
}

export interface BookingStatusMeta {
  label: string;
  bgClass: string;
  textClass: string;
  borderClass: string;
}

export const BOOKING_STATUS_META: Record<BookingStatus, BookingStatusMeta> = {
  PENDING_FORMALIZATION:  { label: 'Formalizar',          bgClass: 'bg-amber-50',   textClass: 'text-amber-700',   borderClass: 'border-amber-400' },
  PENDING_VERIFICATION:   { label: 'Verificación',        bgClass: 'bg-slate-100',  textClass: 'text-slate-700',   borderClass: 'border-slate-400' },
  CONFIRMED:              { label: 'Confirmada',          bgClass: 'bg-blue-50',    textClass: 'text-blue-700',    borderClass: 'border-blue-400'  },
  ACTIVE:                 { label: 'Activa',              bgClass: 'bg-emerald-50', textClass: 'text-emerald-700', borderClass: 'border-emerald-500'},
  CANCELLED:              { label: 'Cancelada',           bgClass: 'bg-red-50',     textClass: 'text-red-600',     borderClass: 'border-red-400'   },
  COMPLETED:              { label: 'Completada',          bgClass: 'bg-gray-50',    textClass: 'text-gray-500',    borderClass: 'border-gray-300'  },
  MANUAL_BLOCK:           { label: 'Bloqueado',           bgClass: 'bg-slate-100',  textClass: 'text-slate-600',   borderClass: 'border-slate-400' },
};

/** True when the owner panel should surface this booking. */
export function bookingNeedsOwnerAction(booking: Booking): boolean {
  switch (booking.status) {
    case 'PENDING_FORMALIZATION':
      return true;   // user paid — slot is blocked; owner can track it
    case 'PENDING_VERIFICATION':
      return true;   // owner must verify payment receipt
    case 'CONFIRMED':
      return true;
    case 'ACTIVE':
      if (booking.securityDepositSkippedByOwnerAt) {
        return true;
      }
      return !booking.securityDepositReturnedAt;
    default:
      return false;
  }
}

export const CALENDAR_BLOCKING_STATUSES: BookingStatus[] = [
  'PENDING_FORMALIZATION',
  'PENDING_VERIFICATION',
  'CONFIRMED',
  'ACTIVE',
  'MANUAL_BLOCK',
];

/** Bookings that occupy slots on the public booking calendar. */
export function isCalendarBlockingStatus(status: string | undefined): boolean {
  const normalized = (status ?? '').toUpperCase();
  return (CALENDAR_BLOCKING_STATUSES as readonly string[]).includes(normalized);
}

/** Statuses shown on the owner space calendar grid (blocking + completed history). */
export const OWNER_CALENDAR_VISIBLE_STATUSES: readonly BookingStatus[] = [
  ...CALENDAR_BLOCKING_STATUSES,
  'COMPLETED',
];

export function isOwnerCalendarVisibleStatus(status: string | undefined): boolean {
  return !!status && (OWNER_CALENDAR_VISIBLE_STATUSES as readonly string[]).includes(status);
}

/** Bookings in these statuses prevent deleting the space from the asset list. */
export const SPACE_DELETE_BLOCKING_STATUSES: readonly BookingStatus[] = [
  'PENDING_FORMALIZATION',
  'PENDING_VERIFICATION',
  'CONFIRMED',
  'ACTIVE',
  'COMPLETED',
];

export function isSpaceDeleteBlockingStatus(status: string | undefined): boolean {
  return !!status && (SPACE_DELETE_BLOCKING_STATUSES as readonly string[]).includes(status);
}

export interface Booking {
  id?: string;
  spaceId: string;
  /** Canonical owner id (same as space.ownerId). */
  ownerUid: string;
  /** Legacy/alternate field written by some flows — prefer ownerUid. */
  ownerId?: string;
  userId: string;
  date: string;
  startTime: string;
  endTime: string;
  status: BookingStatus;
  label?: string;
  guestName?: string;
  totalPrice?: number;
  spaceName?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;

  paymentTimestamp?: Timestamp;
  platformFeeAcceptanceLog?: PlatformFeeAcceptanceLog;
  /** Absolute deadline to sign + upload receipt (from payment + formalizationWindowDays). */
  formalizationDeadline?: Timestamp;
  /** 0 = hasta 1 h antes del evento; 1, 3 o 7 = días desde el pago. */
  formalizationWindowDays?: FormalizationWindowDays;
  formalizationDeadlineMode?: FormalizationDeadlineMode;
  daysUntilEventAtPayment?: number;
  /** Copied from propietario `users/{uid}` (Ajustes → Cobros) at payment. */
  ownerAccountHolder?: string;
  ownerIban?: string;
  ownerMobilePhone?: string;
  /**
   * Snapshot of which payment methods the owner had active at booking time.
   * Used by the formalization UI to render only the enabled methods even
   * if the owner toggles one off later.
   */
  ownerSepaEnabled?: boolean;
  ownerBizumEnabled?: boolean;
  ownerCashEnabled?: boolean;

  signedContractSnapshotHtml?: string;
  userLegalName?: string;
  userDni?: string;

  /** Optional guest identity defaults; TimeFlexRent does not issue a tax invoice to the guest. */
  guestBillingLegalName?: string;
  guestBillingTaxId?: string;
  guestBillingEntityType?: 'persona_fisica' | 'persona_juridica';
  guestBillingActsAsBusiness?: boolean;
  guestBillingFiscalRegime?: 'empresa_autonomo' | 'particular';
  guestBillingAddress?: {
    line1: string;
    postalCode: string;
    city: string;
    province: string;
    country: 'ES';
  };
  /** Owner fiscal snapshot for TimeFlexRent's commission invoice. */
  ownerBillingLegalName?: string;
  ownerBillingTaxId?: string;
  ownerBillingEntityType?: 'persona_fisica' | 'persona_juridica';
  ownerBillingActsAsBusiness?: boolean;
  ownerBillingFiscalRegime?: 'empresa_autonomo' | 'particular';
  ownerBillingAddress?: {
    line1: string;
    postalCode: string;
    city: string;
    province: string;
    country: 'ES';
  };
  userPhone?: string;
  userIp?: string;
  formalizationTimestamp?: Timestamp;
  contractSignedAt?: Timestamp;

  depositReceiptUrl?: string;
  depositReceiptSubmittedAt?: Timestamp;
  /** Urgent MANUAL formalization without receipt (Bizum, ≤1 day to event). */
  fastTrackBizum?: boolean;
  fastTrackAt?: Timestamp;
  /** Owner sent the booking back to formalization (bad receipt). */
  depositReceiptRejectedAt?: Timestamp;
  depositReceiptRejectionNote?: string;
  ownerConfirmedAt?: Timestamp;
  /** Owner marked refundable security deposit received (on-site / before event). */
  securityDepositReceivedAt?: Timestamp;
  securityDepositReceivedMethod?: SecurityDepositReceivedMethod;
  /** Owner started the event day (CONFIRMED → ACTIVE). */
  activatedAt?: Timestamp;
  /** Owner returned security deposit after event (ACTIVE → COMPLETED). */
  securityDepositReturnedAt?: Timestamp;
  /**
   * Owner started ACTIVE without guest deposit confirmed (audit).
   * When set, do not offer "return deposit" — use complete-without-deposit instead.
   */
  securityDepositSkippedByOwnerAt?: Timestamp;
  securityDepositSkippedByOwnerUid?: string;
  /** T-48h deposit sub-state (see booking_workflow.md § Fase D). */
  securityDepositStatus?:
    | 'not_due'
    | 'awaiting_guest'
    | 'submitted'
    | 'confirmed'
    | 'charged'
    | 'waived'
    | 'skipped_by_owner';
  /** Guest-selected method for the security deposit step. */
  securityDepositMethod?: SecurityDepositMethod;
  securityDepositReceiptUrl?: string;
  securityDepositSubmittedAt?: Timestamp;
  /** Amount captured (in cents) when owner charges a card_hold deposit. */
  securityDepositChargedCents?: number;
  securityDepositChargedAt?: Timestamp;
  stripeDepositCheckoutSessionId?: string;
  stripeDepositPaymentIntentId?: string;
  cancelledAt?: Timestamp;
  cancellationReason?: string;

  bookingPriceCents?: number;
  depositCents?: number;
  totalAmountCents?: number;
  commissionAmountCents?: number;
  /** Net commission collected via Stripe application_fee (after IRPF adjustment). */
  applicationFeeCents?: number;
  commissionRateApplied?: number;
  ownerEntityTypeAtCheckout?: 'persona_fisica' | 'persona_juridica';
  ownerActsAsBusinessAtCheckout?: boolean;
  platformIssuerTypeAtCheckout?: 'self_employed' | 'company';
  vatRateAppliedAtCheckout?: number;
  /** Legacy snapshot retained for bookings created before the fiscal migration. */
  ownerFiscalRegimeAtCheckout?: 'empresa_autonomo' | 'particular';
  irpfRateAppliedAtCheckout?: number;
  /** Platform commission invoice to owner (issued by Cloud Functions). */
  commissionInvoiceId?: string;
  commissionInvoiceNumber?: string;
  paymentFulfillmentAt?: Timestamp;
  /** Guest payment receipt email (not a tax invoice). */
  paymentConfirmationEmailSentAt?: Timestamp;
  /** Stripe processing fee in cents (balance_transaction.fee). Stamped by webhook after payment. */
  stripeFeeAmountCents?: number;
  /** Direct Connect charges debit the owner; platform charges debit TimeFlexRent. */
  stripeFeeBearer?: 'OWNER' | 'PLATFORM';
  stripePaymentIntentId?: string;
  stripeCheckoutSessionId?: string;
  stripeDestinationAccount?: string;
  paymentConfirmedAt?: Timestamp;
  customerEmail?: string;

  contractAcceptedAt?: Timestamp;
  contractAcceptanceIp?: string;
  contractAcceptanceUserAgent?: string;

  payment_status?: PaymentStatus;
  payoutModeAtCheckout?: 'MANUAL' | 'STRIPE';
  securityDepositDueAt?: Timestamp;
  stripeRefundId?: string;
  refundedAt?: Timestamp;
}

export type PaymentStatus = 'COMMISSION_ONLY' | 'FULL_PAYMENT';

/** How the owner received the refundable security deposit (CONFIRMED tab). */
export type SecurityDepositReceivedMethod = 'transfer' | 'bizum' | 'cash';

/** Guest choice for the T-48h security deposit step. */
export type SecurityDepositMethod = SecurityDepositReceivedMethod | 'card_hold';

export const SECURITY_DEPOSIT_RECEIVED_METHOD_LABELS: Record<SecurityDepositReceivedMethod, string> = {
  transfer: 'Transferencia',
  bizum: 'Bizum',
  cash: 'En efectivo',
};

export const SECURITY_DEPOSIT_METHOD_LABELS: Record<SecurityDepositMethod, string> = {
  ...SECURITY_DEPOSIT_RECEIVED_METHOD_LABELS,
  card_hold: 'Retención en tarjeta',
};

/** True when the guest must complete the security deposit step in Mis reservas. */
export function guestNeedsSecurityDepositAction(booking: Booking): boolean {
  return booking.status === 'CONFIRMED'
    && (booking.depositCents ?? 0) > 0
    && booking.securityDepositStatus === 'awaiting_guest';
}

/** True when deposit is secured (manual confirm, card hold, or owner marked cash). */
export function isSecurityDepositSecured(booking: Booking): boolean {
  if ((booking.depositCents ?? 0) <= 0) return true;
  if (booking.securityDepositStatus === 'waived') return true;
  if (booking.securityDepositStatus === 'skipped_by_owner') return true;
  if (booking.securityDepositStatus === 'confirmed') return true;
  return !!booking.securityDepositReceivedAt;
}

/** Sort bookings by event date/time, nearest upcoming first. */
export function compareBookingsByEventAsc(a: Booking, b: Booking): number {
  const dateCmp = a.date.localeCompare(b.date);
  return dateCmp !== 0 ? dateCmp : a.startTime.localeCompare(b.startTime);
}
